import React, { useState, useEffect } from "react";
import { ArrowLeft, Trash2 } from "lucide-react";

// Import utility functions from components.js
import { getParliamentaryTerms, getAllPetitions, AUTHORIZED_USER_ID, formatTermDisplay, createParliamentaryTerm, deleteParliamentaryTerm, updateParliamentaryTerm, getBanStats, getBannedUsers, checkUserBan, refreshBanCache } from "./components";


export const TermManagement = ({ user, onBack }) => {
  const [terms, setTerms] = useState([]);
  const [termSearch, setTermSearch] = useState('');
  const [allPetitions, setAllPetitions] = useState([]);
  const [termStats, setTermStats] = useState({}); // Store petition counts for each term
  const [loading, setLoading] = useState(true);
  const [selectedPetitions, setSelectedPetitions] = useState([]);
  const [showCreateTerm, setShowCreateTerm] = useState(false);
  const [showEditTerm, setShowEditTerm] = useState(false);
  const [editingTerm, setEditingTerm] = useState(null);
  
  // Ban Management States
  const [banStats, setBanStats] = useState({
    total_banned_users: 0,
    service_available: false,
    cache_info: null
  });
  const [checkUserId, setCheckUserId] = useState('');
  const [banCheckResult, setBanCheckResult] = useState(null);
  const [showBanList, setShowBanList] = useState(false);
  const [bannedUsers, setBannedUsers] = useState([]);


  // New term form data
  const [newTerm, setNewTerm] = useState({
    name: '',
    startDate: '',
    endDate: ''
  });

  //  Management Functions
  const loadStats = async () => {
    setLoading(true);
    try {
      const stats = await getStats();
      setStats(stats);
    } catch (error) {
      console.error('Error loading  stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const refreshCacheHandler = async () => {
    setLoading(true);
    try {
      await refreshCache();
      await loadStats();
    } catch (error) {
      console.error('Error refreshing  cache:', error);
    } finally {
      setLoading(false);
    }
  };

  const checkUserHandler = async () => {
    setLoading(true);
    try {
      const result = await checkUser(checkUserId);
      setCheckResult(result);
    } catch (error) {
      console.error('Error checking user  status:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadnedUsersList = async () => {
    setLoading(true);
    try {
      const result = await getnedUsers();
      setnedUsers(result.ned_users || []);
    } catch (error) {
      console.error('Error loading ned users:', error);
    } finally {
      setLoading(false);
    }
  };

  // Load terms and petitions on component mount
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      try {
        const [fetchedTerms, petitions] = await Promise.all([
          getParliamentaryTerms(),
          getAllPetitions()
        ]);
        
        setTerms(fetchedTerms);
        setAllPetitions(petitions);
        
        // Calculate petition counts and signature totals for each term
        const stats = {};
        fetchedTerms.forEach(term => {
          const termPetitions = petitions.filter(p => String(p.termId) === String(term.id));
          const totalSignatures = termPetitions.reduce((sum, petition) => sum + (petition.signatures || 0), 0);
          stats[term.id] = {
            petitionCount: termPetitions.length,
            totalSignatures: totalSignatures
          };
        });
        setTermStats(stats);
      } catch (error) {
        console.error('Error loading data:', error);
        setTerms([]);
        setAllPetitions([]);
        setTermStats({});
      } finally {
        setLoading(false);
      }
    };
    
    loadData();
    
    // Load  stats on component mount
    loadStats();
  }, []);

  // Refresh data when terms are updated
  useEffect(() => {
    const handleTermsUpdate = async () => {
      const updatedTerms = await getParliamentaryTerms();
      setTerms(updatedTerms);
      try {
        const petitions = await getAllPetitions();
        setAllPetitions(petitions);
        
        // Update petition counts and signature totals for each term
        const stats = {};
        updatedTerms.forEach(term => {
          const termPetitions = petitions.filter(p => p.termId === term.id);
          const totalSignatures = termPetitions.reduce((sum, petition) => sum + (petition.signatures || 0), 0);
          stats[term.id] = {
            petitionCount: termPetitions.length,
            totalSignatures: totalSignatures
          };
        });
        setTermStats(stats);
      } catch (error) {
        console.error('Error loading petitions:', error);
        setAllPetitions([]);
        setTermStats({});
      }
    };
    
    window.addEventListener('termsUpdated', handleTermsUpdate);
    window.addEventListener('petitionsUpdated', handleTermsUpdate);
    
    return () => {
      window.removeEventListener('termsUpdated', handleTermsUpdate);
      window.removeEventListener('petitionsUpdated', handleTermsUpdate);
    };
  }, []);



  const handleCreateTerm = async () => {
    if (!newTerm.name || !newTerm.startDate || !newTerm.endDate) {
      alert('Please fill in all fields');
      return;
    }

    const newTermData = {
      name: newTerm.name,
      startDate: newTerm.startDate,
      endDate: newTerm.endDate,
      status: 'upcoming'
    };

    try {
      const result = await createParliamentaryTerm(newTermData);
      
      if (result.success) {
        // Refresh terms list
        const updatedTerms = await getParliamentaryTerms();
        setTerms(updatedTerms);
        
        setNewTerm({ name: '', startDate: '', endDate: '' });
        setShowCreateTerm(false);
        
        alert('Parliamentary term created successfully!');
      } else {
        alert(result.message || 'Failed to create parliamentary term');
      }
    } catch (error) {
      console.error('Error creating term:', error);
      alert('Failed to create parliamentary term. Please try again.');
    }
  };

  const handleEditTerm = async () => {
    if (!editingTerm.name || !editingTerm.startDate || !editingTerm.endDate) {
      alert('Please fill in all fields');
      return;
    }

    try {
      const updateData = {
        name: editingTerm.name,
        startDate: editingTerm.startDate,
        endDate: editingTerm.endDate,
        status: editingTerm.status
      };
      
      const result = await updateParliamentaryTerm(editingTerm.id, updateData);
      
      if (result.success) {
        // Refresh terms list
        const updatedTerms = await getParliamentaryTerms();
        setTerms(updatedTerms);
        
        setEditingTerm(null);
        setShowEditTerm(false);
        
        alert('Parliamentary term updated successfully!');
      } else {
        alert(result.message || 'Failed to update parliamentary term');
      }
    } catch (error) {
      console.error('Error updating term:', error);
      alert('Failed to update parliamentary term. Please try again.');
    }
  };

  const handleDeleteAllPetitions = () => {
    if (window.confirm('⚠️ WARNING: This will permanently delete ALL petitions in the system. This action cannot be undone. Are you absolutely sure?')) {
      if (window.confirm('Are you REALLY sure? This will delete all user-created petitions permanently!')) {
        // Clear all user-created petitions from localStorage
        localStorage.removeItem('user_petitions');
        
        // Reset state
        setAllPetitions(getAllPetitions()); // This will now only show mock petitions
        setSelectedPetitions([]);
        
        // Trigger global update
        window.dispatchEvent(new CustomEvent('petitionsUpdated'));
        
        alert('All petitions have been deleted successfully.');
      }
    }
  };


  // Check if user is System Administrator only
  const isSystemAdmin = (user) => {
    if (!user) return false;
    return user.id === AUTHORIZED_USER_ID || user.id === AUTHORIZED_USER_ID.toString() || user.id === '1065731316';
  };

  if (!isSystemAdmin(user)) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-red-600 mb-4">Access Denied</h2>
          <p className="text-gray-600">Only System Administrators can access term management.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading term management...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl">
      <div className="mb-6">
        <button
          onClick={onBack}
          className="text-blue-600 hover:text-blue-800 mb-4"
        >
          ← Back to petitions
        </button>
        <h1 className="text-3xl font-bold text-gray-900">Parliamentary Term Management</h1>
        <p className="text-gray-600 mt-2">Manage parliamentary terms and assign petitions to appropriate terms</p>
      </div>

      {/* Term Management Section */}
      <div className="bg-white rounded-lg shadow-lg p-6 mb-8">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold text-gray-900">Parliamentary Terms</h2>
          <button
            onClick={() => setShowCreateTerm(true)}
            className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
          >
            Create New Term
          </button>
        </div>

        <input
          type="text"
          value={termSearch}
          onChange={(e) => setTermSearch(e.target.value)}
          placeholder="Search terms..."
          data-testid="term-search-input"
          className="w-full mb-4 px-3 py-2 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        />

        <div className="max-h-[60vh] overflow-y-auto space-y-2 pr-1">
          {terms
            .filter(term => formatTermDisplay(term).toLowerCase().includes(termSearch.toLowerCase()))
            .map(term => (
            <div key={term.id} className="border rounded-lg px-4 py-2 flex justify-between items-center gap-4">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-xs px-2 py-0.5 rounded capitalize ${
                    term.status === 'current' ? 'bg-green-100 text-green-800' :
                    term.status === 'upcoming' ? 'bg-blue-100 text-blue-800' :
                    'bg-gray-100 text-gray-800'
                  }`}>
                    {term.status}
                  </span>
                  <h3 className="font-semibold text-base text-gray-900 truncate">{formatTermDisplay(term)}</h3>
                </div>
                <div className="flex items-center gap-x-3 text-xs text-gray-500 mt-0.5">
                  <span>Petitions: {(termStats[term.id]?.petitionCount || 0)}</span>
                  <span>Signatures: {(termStats[term.id]?.totalSignatures || 0).toLocaleString()}</span>
                </div>
              </div>
              <div className="flex space-x-2 shrink-0">
                <button
                  onClick={() => {
                    setEditingTerm({ ...term });
                    setShowEditTerm(true);
                  }}
                  className="bg-orange-600 text-white px-3 py-1 rounded hover:bg-orange-700 text-sm"
                >
                  Edit
                </button>
                <button
                  onClick={async () => {
                    console.log('Delete button clicked for term:', term.id);
                    const petitionCount = termStats[term.id]?.petitionCount || 0;
                    let confirmMessage = 'Are you sure you want to delete this parliamentary term?';
                    
                    if (petitionCount > 0) {
                      confirmMessage = `This term has ${petitionCount} petition(s). Deleting this term will unassign these petitions. Are you sure?`;
                    }
                    
                    console.log('Showing confirm dialog:', confirmMessage);
                    if (window.confirm(confirmMessage)) {
                      try {
                        console.log('User confirmed, calling deleteParliamentaryTerm with ID:', term.id);
                        const result = await deleteParliamentaryTerm(term.id);
                        console.log('Delete result:', result);
                        
                        if (result.success) {
                          // Refresh terms list
                          console.log('Delete successful, refreshing terms list');
                          const updatedTerms = await getParliamentaryTerms();
                          console.log('Updated terms after deletion:', updatedTerms);
                          setTerms(updatedTerms);
                          console.log('Terms state updated');
                          alert('Term deleted successfully.');
                        } else {
                          console.error('Delete failed:', result);
                          alert(result.message || 'Failed to delete parliamentary term');
                        }
                      } catch (error) {
                        console.error('Error deleting term:', error);
                        alert('Failed to delete parliamentary term. Please try again.');
                      }
                    } else {
                      console.log('User cancelled deletion');
                    }
                  }}
                  className="bg-red-600 text-white px-3 py-1 rounded hover:bg-red-700 text-sm"
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Create Term Form */}
        {showCreateTerm && (
          <div className="mt-6 p-4 bg-gray-50 border rounded">
            <h3 className="font-semibold mb-4">Create New Parliamentary Term</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Term Name</label>
                <input
                  type="text"
                  value={newTerm.name}
                  onChange={(e) => setNewTerm({...newTerm, name: e.target.value})}
                  placeholder="e.g., Term 88"
                  className="w-full p-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Start Date</label>
                <input
                  type="date"
                  value={newTerm.startDate}
                  onChange={(e) => setNewTerm({...newTerm, startDate: e.target.value})}
                  className="w-full p-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">End Date</label>
                <input
                  type="date"
                  value={newTerm.endDate}
                  onChange={(e) => setNewTerm({...newTerm, endDate: e.target.value})}
                  className="w-full p-2 border rounded"
                />
              </div>
            </div>
            <div className="flex space-x-2 mt-4">
              <button
                onClick={handleCreateTerm}
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
              >
                Create Term
              </button>
              <button
                onClick={() => {
                  setShowCreateTerm(false);
                  setNewTerm({ name: '', startDate: '', endDate: '' });
                }}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Edit Term Form */}
        {showEditTerm && editingTerm && (
          <div className="mt-6 p-4 bg-gray-50 border rounded">
            <h3 className="font-semibold mb-4">Edit Parliamentary Term</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-1">Term Name</label>
                <input
                  type="text"
                  value={editingTerm.name}
                  onChange={(e) => setEditingTerm({...editingTerm, name: e.target.value})}
                  className="w-full p-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Start Date</label>
                <input
                  type="date"
                  value={editingTerm.startDate}
                  onChange={(e) => setEditingTerm({...editingTerm, startDate: e.target.value})}
                  className="w-full p-2 border rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">End Date</label>
                <input
                  type="date"
                  value={editingTerm.endDate}
                  onChange={(e) => setEditingTerm({...editingTerm, endDate: e.target.value})}
                  className="w-full p-2 border rounded"
                />
              </div>
            </div>
            <div className="flex space-x-2 mt-4">
              <button
                onClick={handleEditTerm}
                className="bg-green-600 text-white px-4 py-2 rounded hover:bg-green-700"
              >
                Save Changes
              </button>
              <button
                onClick={() => {
                  setShowEditTerm(false);
                  setEditingTerm(null);
                }}
                className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>

      {/*  Management Section */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-lg p-6">
        {/*  Management System */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 mb-4">
          <h3 className="font-semibold text-gray-800 mb-2">Ban Management</h3>
          <p className="text-gray-700 text-sm mb-4">
            Users are loaded from Google Sheets
          </p>

          {/*  Stats */}
          <div className="bg-gray-50 p-3 rounded mb-4">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium">Total ned Users:</span>
                <span className="ml-2 text-red-600">{banStats.total_banned_users || 0}</span>
              </div>
              <div>
                <span className="font-medium">System Status:</span>
                <span className={`ml-2 ${banStats.service_available ? 'text-green-600' : 'text-orange-600'}`}>
                  {banStats.service_available ? 'Active' : 'Unavailable'}
                </span>
              </div>
            </div>
          </div>

          {/* Ban Actions */}
          <div className="flex gap-2 mb-4">
            <button
              onClick={loadStats}
              disabled={loading}
              className="bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700 text-sm"
            >
              Refresh Bans
            </button>
          </div>
        </div>
      </div>
    </div>           
  );
};
